import { arrayWithoutSubset, updateRecord } from '@ms/yammer-data-support';

import { OfficeTopicLoadAuthorizedFulfilledAction } from '../../domains/cortexTopics/actions.types';
import {
  PublisherTopicAddedAction,
  PublisherTopicBarClosedAction,
  PublisherTopicBarOpenedAction,
  PublisherTopicRemovedAction,
} from '../../domains/publishers/actions.types';
import {
  CreateCuratedTopicFulfilledAction,
  TopicCreateFulfilledAction,
  TopicCurateFulfilledAction,
} from '../../domains/topics/actions.types';
import { Publishers } from '../types';

import { PublishersAction } from './reducer.types';

const openTopicBar = (state: Publishers, { id }: PublisherTopicBarOpenedAction['payload']) =>
  updateRecord(state, id, { isTopicBarOpen: true });

const closeTopicBar = (state: Publishers, { id }: PublisherTopicBarClosedAction['payload']) =>
  updateRecord(state, id, { isTopicBarOpen: false });

const addUnpublishedTopicByName = (
  state: Publishers,
  { publisherId, topicName }: PublisherTopicAddedAction['payload']
) => {
  const existingTopicNames = state[publisherId].topicNames;

  if (existingTopicNames.includes(topicName)) {
    return state;
  }

  return updateRecord(state, publisherId, { topicNames: [...existingTopicNames, topicName] });
};

const addUnpublishedTopic = (
  state: Publishers,
  { publisherId, topicId, topicName }: PublisherTopicAddedAction['payload']
) => {
  if (!topicId) {
    return addUnpublishedTopicByName(state, { publisherId, topicName, topicId });
  }

  const existingTopicIds = state[publisherId].topicIds;

  if (existingTopicIds.includes(topicId)) {
    return state;
  }

  return updateRecord(state, publisherId, { topicIds: [...existingTopicIds, topicId] });
};

type AddUnpublishedOfficeTopic = (
  state: Publishers,
  payload: OfficeTopicLoadAuthorizedFulfilledAction['payload']
) => Publishers;
const addUnpublishedOfficeTopic: AddUnpublishedOfficeTopic = (state, { publisherId, entities: { topics } }) => {
  if (!topics) {
    return state;
  }

  const addedTopics = Object.values(topics);

  if (addedTopics.length === 0) {
    return state;
  }

  const { topicIds: existingTopicIds } = state[publisherId];
  const addedTopicId = addedTopics[0].id;

  if (existingTopicIds.includes(addedTopicId)) {
    return state;
  }

  return updateRecord(state, publisherId, { ...state[publisherId], topicIds: [...existingTopicIds, addedTopicId] });
};

const removeUnpublishedTopicByName = (
  state: Publishers,
  { publisherId, topicName }: PublisherTopicRemovedAction['payload']
) =>
  updateRecord(state, publisherId, {
    topicNames: arrayWithoutSubset(state[publisherId].topicNames, [topicName]),
  });

const removeUnpublishedTopic = (
  state: Publishers,
  { publisherId, topicId, topicName }: PublisherTopicRemovedAction['payload']
) => {
  if (!topicId) {
    return removeUnpublishedTopicByName(state, { publisherId, topicName });
  }

  const newTopicIds = arrayWithoutSubset(state[publisherId].topicIds, [topicId]);

  return updateRecord(state, publisherId, { topicIds: newTopicIds });
};

const updateCuratedTopicId = (state: Publishers, payload: TopicCurateFulfilledAction['payload']) => {
  const {
    topicId,
    publisherId,
    topic: { id: payloadTopicId },
  } = payload;

  if (!publisherId) {
    return state;
  }

  const existingTopicIds = state[publisherId].topicIds;

  const updatedTopicIds = existingTopicIds.map((existingTopicId) =>
    existingTopicId === topicId ? payloadTopicId : existingTopicId
  );

  return updateRecord(state, publisherId, { topicIds: updatedTopicIds });
};

const addTopicToPublisherAfterCreate = (
  state: Publishers,
  payload: CreateCuratedTopicFulfilledAction['payload'] | TopicCreateFulfilledAction['payload']
) => {
  if (payload.publisherId) {
    const {
      id,
      publisherId,
      entities: { topics },
    } = payload;

    return addUnpublishedTopic(state, { topicId: id, publisherId, topicName: topics[id].name });
  }

  return state;
};

type TopicActionsReducer = (state: Publishers, action: PublishersAction) => Publishers;
export const topicActionsReducer: TopicActionsReducer = (state, action) => {
  switch (action.type) {
    case 'PUBLISHER.TOPIC_BAR_OPENED':
      return openTopicBar(state, action.payload);
    case 'PUBLISHER.TOPIC_BAR_CLOSED':
      return closeTopicBar(state, action.payload);
    case 'PUBLISHER.TOPIC_ADDED':
      return addUnpublishedTopic(state, action.payload);
    case 'PUBLISHER.TOPIC_REMOVED':
      return removeUnpublishedTopic(state, action.payload);
    case 'OFFICE_TOPIC.LOAD_FULFILLED':
      return addUnpublishedOfficeTopic(state, action.payload);
    case 'TOPIC.CREATE_AND_CURATE_FULFILLED':
      return addTopicToPublisherAfterCreate(state, action.payload);
    case 'TOPIC.CREATE_FULFILLED':
      return addTopicToPublisherAfterCreate(state, action.payload);
    case 'TOPIC.CURATE_FULFILLED':
      return updateCuratedTopicId(state, action.payload);
    default:
      return state;
  }
};
